import { useMutation } from '@apollo/client';
import { DELETE_COMMUNITY_GROUP_TOPIC, GET_COMMUNITY_GROUP_TOPICS } from '@communities/content-management/graphql';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { useCallback, useState } from 'react';

interface DeleteCommunityGroupTopicResponse {
  deleteCommunityGroupTopic: boolean;
}

export function useDeleteCommunityGroupTopic(communityGroupId?: string) {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [deleteTopicMutation, { loading }] = useMutation<DeleteCommunityGroupTopicResponse>(DELETE_COMMUNITY_GROUP_TOPIC, {
    refetchQueries: communityGroupId ? [{ query: GET_COMMUNITY_GROUP_TOPICS, variables: { communityGroupId } }] : [],
    awaitRefetchQueries: true,
  });

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const deleteTopic = async (communityGroupId: string, topicName: string): Promise<boolean> => {
    try {
      track('DeleteCommunityGroupTopicInitiated', {
        communityGroupId,
        topicName,
      });

      await deleteTopicMutation({
        variables: {
          communityGroupId,
          topicName,
        },
      });

      track('DeleteCommunityGroupTopicSucceeded', {
        communityGroupId,
        topicName,
      });

      return true;
    } catch (error) {
      track('DeleteCommunityGroupTopicFailed', {
        communityGroupId,
        topicName,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setErrors(transformErrors(error));

      return false;
    }
  };

  return {
    deleteTopic,
    loading,
    errors,
    resetErrors,
  };
}

export default useDeleteCommunityGroupTopic;
